import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Plus } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { StatusBadge } from '@/components/hr/status-badge'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

// ── Types ─────────────────────────────────────────────────────────────────────

type ApplicantStatus = 'applied' | 'screening' | 'interview' | 'offer' | 'hired' | 'rejected' | 'withdrawn'

type Applicant = {
  id: number
  openingId: number
  firstName: string
  lastName: string
  email: string
  phone: string | null
  source: string | null
  status: ApplicantStatus
  rating: number | null
  notes: string | null
}

type JobOpening = {
  id: number
  title: string
  departmentId: number | null
  departmentName: string | null
  description: string | null
  requirements: string | null
  employmentType: string | null
  salaryRangeMin: string | null
  salaryRangeMax: string | null
  positionsCount: number | null
  status: string
  closesAt: string | Date | null
  applicantCount: number
}

// ── Schemas ───────────────────────────────────────────────────────────────────

const addApplicantSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Valid email is required'),
  phone: z.string().optional(),
  coverLetter: z.string().optional(),
  source: z.enum(['website', 'referral', 'linkedin', 'agency', 'other']),
})

type AddApplicantFormValues = z.infer<typeof addApplicantSchema>

// ── Constants ─────────────────────────────────────────────────────────────────

const PIPELINE_COLUMNS: { status: ApplicantStatus; label: string }[] = [
  { status: 'applied', label: 'Applied' },
  { status: 'screening', label: 'Screening' },
  { status: 'interview', label: 'Interview' },
  { status: 'offer', label: 'Offer' },
  { status: 'hired', label: 'Hired' },
]

const sourceColors: Record<string, string> = {
  website: 'bg-blue-100 text-blue-800',
  referral: 'bg-green-100 text-green-800',
  linkedin: 'bg-indigo-100 text-indigo-800',
  agency: 'bg-purple-100 text-purple-800',
  other: 'bg-gray-100 text-gray-800',
}

// ── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/hr/recruitment/openings/$openingId')({
  component: OpeningDetailPage,
})

function OpeningDetailPage() {
  const params = Route.useParams()
  const openingId = Number(params.openingId)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: openingRaw, isLoading } = useQuery(
    orpc.hr.getJobOpening.queryOptions({ input: { id: openingId } }),
  )

  const { data: applicantsData, isLoading: applicantsLoading } = useQuery(
    orpc.hr.listApplicants.queryOptions({ input: { openingId, page: 1, limit: 100 } }),
  )

  const opening = openingRaw as JobOpening | null
  const applicants = (applicantsData?.data ?? []) as Applicant[]

  const [isAddApplicantOpen, setIsAddApplicantOpen] = useState(false)
  const [draggedId, setDraggedId] = useState<number | null>(null)

  const updateStatusMutation = useMutation(
    orpc.hr.updateApplicantStatus.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [['hr', 'listApplicants']] })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const updateOpeningMutation = useMutation(
    orpc.hr.updateJobOpening.mutationOptions({
      onSuccess: () => {
        toast.success('Opening updated')
        queryClient.invalidateQueries({ queryKey: [['hr', 'getJobOpening']] })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const createApplicantMutation = useMutation(
    orpc.hr.createApplicant.mutationOptions({
      onSuccess: () => {
        toast.success('Applicant added')
        queryClient.invalidateQueries({ queryKey: [['hr', 'listApplicants']] })
        setIsAddApplicantOpen(false)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const handleDragStart = (applicantId: number) => {
    setDraggedId(applicantId)
  }

  const handleDrop = (targetStatus: ApplicantStatus) => {
    if (draggedId === null) return
    const applicant = applicants.find((a) => a.id === draggedId)
    if (!applicant || applicant.status === targetStatus) {
      setDraggedId(null)
      return
    }
    updateStatusMutation.mutate({ id: draggedId, status: targetStatus })
    setDraggedId(null)
  }

  if (isLoading) {
    return (
      <div className="container mx-auto py-6 px-4">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-48 w-full mb-6" />
        <div className="grid grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    )
  }

  if (!opening) {
    return (
      <div className="container mx-auto py-6 px-4">
        <p className="text-muted-foreground">Job opening not found</p>
      </div>
    )
  }

  const salaryRange =
    opening.salaryRangeMin && opening.salaryRangeMax
      ? `${Number(opening.salaryRangeMin).toLocaleString()} – ${Number(opening.salaryRangeMax).toLocaleString()}`
      : opening.salaryRangeMin
        ? `From ${Number(opening.salaryRangeMin).toLocaleString()}`
        : opening.salaryRangeMax
          ? `Up to ${Number(opening.salaryRangeMax).toLocaleString()}`
          : null

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-4 flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => window.history.back()}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Opening header card */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-xl">{opening.title}</CardTitle>
              <CardDescription className="mt-1">
                {opening.departmentName && (
                  <span className="mr-3">{opening.departmentName}</span>
                )}
                {opening.employmentType && (
                  <Badge variant="outline" className="capitalize mr-2">
                    {opening.employmentType.replace('_', ' ')}
                  </Badge>
                )}
                {salaryRange && (
                  <span className="text-sm text-muted-foreground">{salaryRange}</span>
                )}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={opening.status} />
              {opening.status === 'draft' && (
                <Button
                  size="sm"
                  onClick={() =>
                    updateOpeningMutation.mutate({ id: opening.id, status: 'open' })
                  }
                  disabled={updateOpeningMutation.isPending}
                >
                  Publish
                </Button>
              )}
              {opening.status === 'open' && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      updateOpeningMutation.mutate({ id: opening.id, status: 'on_hold' })
                    }
                    disabled={updateOpeningMutation.isPending}
                  >
                    Put on Hold
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600"
                    onClick={() =>
                      updateOpeningMutation.mutate({ id: opening.id, status: 'closed' })
                    }
                    disabled={updateOpeningMutation.isPending}
                  >
                    Close
                  </Button>
                </>
              )}
              {opening.status === 'on_hold' && (
                <Button
                  size="sm"
                  onClick={() =>
                    updateOpeningMutation.mutate({ id: opening.id, status: 'open' })
                  }
                  disabled={updateOpeningMutation.isPending}
                >
                  Reopen
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        {(opening.description || opening.requirements) && (
          <CardContent className="space-y-4">
            {opening.description && (
              <div>
                <h4 className="font-medium mb-1">Description</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {opening.description}
                </p>
              </div>
            )}
            {opening.requirements && (
              <div>
                <h4 className="font-medium mb-1">Requirements</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {opening.requirements}
                </p>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Applicant pipeline */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Applicant Pipeline</h2>
        <Button size="sm" onClick={() => setIsAddApplicantOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Applicant
        </Button>
      </div>

      {applicantsLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 overflow-x-auto">
          {PIPELINE_COLUMNS.map((col) => {
            const colApplicants = applicants.filter((a) => a.status === col.status)
            return (
              <div
                key={col.status}
                className="min-h-[200px] rounded-lg border bg-muted/30 p-3"
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(col.status)}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium">{col.label}</span>
                  <Badge variant="secondary" className="text-xs">
                    {colApplicants.length}
                  </Badge>
                </div>
                <div className="space-y-2">
                  {colApplicants.map((applicant) => (
                    <div
                      key={applicant.id}
                      draggable
                      onDragStart={() => handleDragStart(applicant.id)}
                      className="bg-background rounded-md border p-3 cursor-grab active:cursor-grabbing hover:shadow-sm transition-shadow"
                    >
                      <p
                        className="font-medium text-sm cursor-pointer hover:underline"
                        onClick={() => void navigate({ to: `/hr/recruitment/applicants/${applicant.id}` })}
                      >
                        {applicant.firstName} {applicant.lastName}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{applicant.email}</p>
                      <div className="flex items-center gap-1 mt-2">
                        {applicant.source && (
                          <span
                            className={`text-xs px-1.5 py-0.5 rounded capitalize ${sourceColors[applicant.source] ?? 'bg-gray-100 text-gray-800'}`}
                          >
                            {applicant.source}
                          </span>
                        )}
                        {applicant.rating && (
                          <span className="text-xs text-yellow-500 ml-1">
                            {'★'.repeat(applicant.rating)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <AddApplicantDialog
        open={isAddApplicantOpen}
        onOpenChange={setIsAddApplicantOpen}
        onSubmit={async (data) => {
          await createApplicantMutation.mutateAsync({
            ...data,
            openingId,
          })
        }}
      />
    </div>
  )
}

// ── Add Applicant Dialog ───────────────────────────────────────────────────────

interface AddApplicantDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: AddApplicantFormValues) => Promise<void>
}

function AddApplicantDialog({ open, onOpenChange, onSubmit }: AddApplicantDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<AddApplicantFormValues>({
    resolver: zodResolver(addApplicantSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      coverLetter: '',
      source: 'website',
    },
  })

  const handleSubmit = async (data: AddApplicantFormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit(data)
      form.reset()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Applicant</DialogTitle>
          <DialogDescription>Add a new applicant to this job opening</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Jane" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="jane@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input placeholder="+1 555 000 0000" {...field} value={field.value ?? ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="source"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Source</FormLabel>
                  <Select
                    onValueChange={(v) =>
                      field.onChange(
                        v as 'website' | 'referral' | 'linkedin' | 'agency' | 'other',
                      )
                    }
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select source" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="website">Website</SelectItem>
                      <SelectItem value="referral">Referral</SelectItem>
                      <SelectItem value="linkedin">LinkedIn</SelectItem>
                      <SelectItem value="agency">Agency</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="coverLetter"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cover Letter</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Applicant's cover letter..."
                      rows={3}
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Adding...' : 'Add Applicant'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
